/**
 * Helper functions for working with parent wall IDs
 *
 * All objects that belong to a wall should use `parentWallIds: string[]`
 * as the single source of truth for parent wall references.
 *
 * Three "find parent wall" variants exist because callers receive different
 * input shapes — they could be unified once all callers are migrated to a
 * single shape, but for now each fits a distinct use case:
 *
 *   findParentWall(object, walls)
 *     Flat walls array. Strict — only reads parentWallIds[0].
 *     Use when you already have a walls array on hand (e.g., iterating one room).
 *
 *   findParentWallWithTraversal(object, allObjects, walls)
 *     Flat walls array + flat objects array. Walks the parents chain so child
 *     fixtures (countertops, sinks, etc.) can resolve to a wall via their
 *     ancestors. Use when you have flat arrays and the element may not point
 *     at a wall directly.
 *
 *   findParentWallInStructure(element, structure)
 *     Whole structureData tree. Lenient — checks parentWallIds[0] / parent /
 *     parentId. Walks structure.walls, then per-room walls. Use when you only
 *     have structureData and don't want to flatten it yourself.
 *
 *   findElementAndWallInStructure(elementId, structure)
 *     Search-by-ID variant. Walks rooms looking for the element across
 *     `doors` / `windows` / `openings` / `objects`, returning the element,
 *     room, parent wall, and which array contained it. Use when you have
 *     only an ID and need to write back into the right room slot.
 */

import { apply3DRotationTo2D } from './geometryUtils';

// Re-exported for ergonomics: callers reaching for parent-wall / opening
// predicates find demoed detection here too. Single source lives in
// openingSyncUtils (ESM) / openingSync (CJS mirror).
export { isDemoedOpening } from './openingSyncUtils';

/**
 * Get parent wall IDs from an object.
 *
 * Reads two shapes:
 *   - `parentWallIds: string[]` — used by every object pre-persist and
 *     by all hand-authored / legacy fixtures. The materializer
 *     populates this directly.
 *   - `cabinetWallRefs: [{id, kind}]` — written by the cabinetLayout
 *     adapter (`phaseBToCabinetLayout`) when the layout is applied
 *     and persisted. That adapter strips `parentWallIds` and replaces
 *     it with `cabinetWallRefs`, so any caller that only checks
 *     `parentWallIds` will get an empty list for cabinets the user
 *     applied via the kitchen-layout modal. Only `kind: 'real'` entries
 *     are returned here — peninsula / island refs point at virtual
 *     surfaces that don't have a corresponding `walls[]` entry, and
 *     callers that want them resolve through `cabinetLayout` instead.
 *
 * @param {Object} object
 * @returns {string[]} Array of parent wall IDs
 */
export const getParentWallIds = (object) => {
  if (Array.isArray(object?.parentWallIds) && object.parentWallIds.length > 0) {
    return object.parentWallIds;
  }
  if (Array.isArray(object?.cabinetWallRefs)) {
    return object.cabinetWallRefs
      .filter((ref) => ref?.kind === 'real' && typeof ref.id === 'string')
      .map((ref) => ref.id);
  }
  return [];
};

/**
 * Get primary parent wall ID (first in array)
 * @param {Object} object - Object with parentWallIds field
 * @returns {string|null} First parent wall ID or null
 */
export const getPrimaryParentWallId = (object) => {
  const ids = getParentWallIds(object);
  return ids[0] || null;
};

/**
 * Check if object belongs to a specific wall
 * @param {Object} object - Object with parentWallIds field
 * @param {string} wallId - Wall ID to check
 * @returns {boolean} True if object belongs to the wall
 */
export const belongsToWall = (object, wallId) => {
  return getParentWallIds(object).includes(wallId);
};

/**
 * True for door / window / opening entries that are actually attached to
 * a wall (have parentWallIds[0]). Used to gate canvas drag / cursor /
 * resize-handle logic that treats all wall-mounted opening categories the
 * same way. (Demoed walls are the opening subtype `category.opening.demoed`,
 * so the `category.opening` check already covers them.)
 * @param {Object} item
 * @returns {boolean}
 */
export const isWallMountedOpening = (item) =>
  !!item &&
  !!(item.category?.door || item.category?.window || item.category?.opening) &&
  !!item.parentWallIds?.[0];

/**
 * The Firestore array name an opening-family element lives under:
 * 'doors', 'windows', or 'openings'. Demoed walls are the opening
 * subtype (category.opening.demoed) and live in the openings[] array.
 *
 * @param {Object} item - item with .category
 * @returns {'doors' | 'windows' | 'openings'}
 */
export const getOpeningArrayName = (item) =>
  item?.category?.door
    ? 'doors'
    : item?.category?.window
      ? 'windows'
      : 'openings';

/**
 * Find parent wall object from walls array
 * @param {Object} object - Object with parentWallIds field
 * @param {Array} walls - Array of wall objects
 * @returns {Object|null} Parent wall object or null
 */
export const findParentWall = (object, walls) => {
  const parentId = getPrimaryParentWallId(object);
  if (!parentId || !walls?.length) return null;
  return walls.find((wall) => wall.id === parentId);
};

/**
 * Resolve an opening to its canonical structure wall.
 *
 * Openings are parented to one of three wall flavors: a structure wall
 * directly, a room surface wall, or an exterior surface wall. Surface
 * walls carry `alignment.structureWallId` pointing back at the structure
 * wall they sit on. This helper walks one hop if needed so callers always
 * get the structure wall (the canonical centerline + dimensions).
 *
 * @param {Object} opening - Opening with parentWallIds
 * @param {Array} structureWalls - data.structureWalls
 * @param {Array} surfaceWalls - data.walls + data.exterior?.walls
 * @returns {Object|null} Structure wall or null
 */
export const findStructureWall = (opening, structureWalls, surfaceWalls) => {
  const parentId = getPrimaryParentWallId(opening);
  if (!parentId) return null;

  const direct = structureWalls?.find((w) => w.id === parentId);
  if (direct) return direct;

  const surface = surfaceWalls?.find((w) => w.id === parentId);
  const swId = surface?.alignment?.structureWallId;
  if (!swId) return null;
  return structureWalls?.find((w) => w.id === swId) || null;
};

/**
 * Check if object has any parent wall
 * @param {Object} object - Object with parentWallIds field
 * @returns {boolean} True if object has at least one parent wall
 */
export const hasParentWall = (object) => {
  return getParentWallIds(object).length > 0;
};

/**
 * Get all parent walls from walls array
 * @param {Object} object - Object with parentWallIds field
 * @param {Array} walls - Array of wall objects
 * @returns {Array} Array of parent wall objects
 */
export const findAllParentWalls = (object, walls) => {
  const parentIds = getParentWallIds(object);
  if (!parentIds.length || !walls?.length) return [];
  return walls.filter((wall) => parentIds.includes(wall.id));
};

/**
 * Find parent wall by traversing up the parents chain
 * For child objects (countertop, sink, etc.) that don't have parentWallIds directly,
 * this function traverses up the parents chain to find an ancestor with parentWallIds.
 *
 * @param {Object} object - Object to find wall for
 * @param {Array} allObjects - All objects in room for parent lookup
 * @param {Array} walls - Array of wall objects
 * @returns {Object|null} Parent wall object or null
 */
export const findParentWallWithTraversal = (object, allObjects, walls) => {
  if (!object) return null;

  // Check if this object has parentWallIds directly
  const directWallId = getPrimaryParentWallId(object);
  if (directWallId) {
    return walls?.find((wall) => wall.id === directWallId) || null;
  }

  // Traverse up parents chain (new unified system: parents: [{id, type}])
  const parentRef = object.parents?.[0];
  if (parentRef?.id) {
    const parentObj = allObjects?.find((o) => o.id === parentRef.id);
    return findParentWallWithTraversal(parentObj, allObjects, walls);
  }

  // Also check legacy parentId field (used by shower components)
  if (object.parentId) {
    const parentObj = allObjects?.find((o) => o.id === object.parentId);
    return findParentWallWithTraversal(parentObj, allObjects, walls);
  }

  return null;
};

/**
 * Find parent wall by walking a structureData tree (structure-level walls
 * first, then per-room walls). Mirrors the legacy ElementPropertiesPanel's
 * inline lookup. Used by panel components that have structureData rather
 * than a flat walls array.
 *
 * @param {Object} element - Object with parentWallIds / parent / parentId
 * @param {Object} structure - structureData with any of `walls`,
 *   `structureWalls`, `structure.walls`, and/or `rooms[].walls`
 * @returns {Object|null} Parent wall object or null
 */
export const findParentWallInStructure = (element, structure) => {
  if (!element || !structure) return null;
  const parentId =
    element.parentWallIds?.[0] || element.parent || element.parentId;
  if (!parentId) return null;
  // An opening is routinely parented straight to a STRUCTURE wall whose id
  // no room wall shares (a merged or pipeline-built wall). The hosts keep
  // those under different keys — `structureWalls` in 3D and the plan,
  // `structure.walls` in elevations — and `walls` alone is the flattened
  // room walls in the plan. Missing the structure arrays sent the panel
  // into free-object X/Z mode, which walked a window off a 141° wall
  // (2026-09-05). Search all of them; a surface wall is parallel to its
  // structure wall, so whichever copy is found gives the same slide axis.
  const candidates = [
    structure.walls,
    structure.structureWalls,
    structure.structure?.walls,
  ];
  for (const arr of candidates) {
    if (!Array.isArray(arr)) continue;
    const wall = arr.find((w) => w?.id === parentId);
    if (wall) return wall;
  }
  if (Array.isArray(structure.rooms)) {
    for (const room of structure.rooms) {
      const wall = room.walls?.find((w) => w.id === parentId);
      if (wall) return wall;
    }
  }
  return null;
};

/**
 * Search a structureData tree for an element by ID, returning the element,
 * the room containing it, the parent wall (if any), and which per-room
 * array (`doors` / `windows` / `openings` / `objects`) the element lives in.
 * Used by handlers that need to write a new position back into the right
 * room slot (e.g., centering, set-position).
 *
 * Returns null if no element with that ID is found in any room.
 *
 * @param {string} elementId
 * @param {Object} structure - structureData with `rooms[]`
 * @returns {{element, room, parentWall, arrayKey}|null}
 */
export const findElementAndWallInStructure = (elementId, structure) => {
  if (!structure?.rooms) return null;
  for (const room of structure.rooms) {
    for (const arrayKey of ['doors', 'windows', 'openings', 'objects']) {
      const element = room[arrayKey]?.find((e) => e.id === elementId);
      if (!element) continue;
      const parentId =
        element.parentWallIds?.[0] || element.parent || element.parentId;
      const parentWall = parentId
        ? room.walls?.find((w) => w.id === parentId) || null
        : null;
      return { element, room, parentWall, arrayKey };
    }
  }
  return null;
};

/**
 * Project the element's center onto its parent wall axis to get a 0–1
 * position. Used by ElementMovementControls for distance-to-wall display
 * and preset-position buttons. Returns 0.5 when the element has no parent
 * wall (the component falls back to a fake wall in that case).
 *
 * @param {Object} element - Object with `center` and parent-wall reference
 * @param {Object} structure - structureData containing the parent wall
 * @returns {number} Position along the wall in [0, 1]
 */
export const calculateElementPositionInStructure = (element, structure) => {
  if (!element || !structure) return 0.5;
  const parentWall = findParentWallInStructure(element, structure);
  if (!parentWall) return 0.5;
  const wallLen = parentWall.dimensions?.x || 10;
  // Slim room-side copies of an opening carry only ids + parentWallIds — no
  // geometry. Without these guards a panel rendering one crashes on deref.
  if (
    typeof parentWall.center?.x !== 'number' ||
    typeof element.center?.x !== 'number'
  ) {
    return 0.5;
  }
  const wallDir = apply3DRotationTo2D(parentWall.rotation);
  const wallStartX = parentWall.center.x - (wallDir[0] * wallLen) / 2;
  const wallStartZ = parentWall.center.z - (wallDir[1] * wallLen) / 2;
  const offsetX = element.center.x - wallStartX;
  const offsetZ = element.center.z - wallStartZ;
  const position = (offsetX * wallDir[0] + offsetZ * wallDir[1]) / wallLen;
  return Math.max(0, Math.min(1, position));
};
